import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
} from "react";
import api from "../config/api";
import { clearCheckoutSession } from "../utils/checkoutRecovery";
import toast from "react-hot-toast";

const AuthContext = createContext();

const initialState = {
  user: null,
  token: null,
  isLoading: true,
  isAuthenticated: false,
};

const authReducer = (state, action) => {
  switch (action.type) {
    case "SET_LOADING":
      return {
        ...state,
        isLoading: action.payload,
      };
    case "LOGIN_SUCCESS":
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
      };
    case "LOGOUT":
      return {
        ...initialState,
        isLoading: false,
      };
    case "UPDATE_USER":
      return {
        ...state,
        user: { ...state.user, ...action.payload },
      };
    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = () => {
      const token = localStorage.getItem("token");
      const user = localStorage.getItem("user");

      if (token && user) {
        try {
          const parsedUser = JSON.parse(user);
          dispatch({
            type: "LOGIN_SUCCESS",
            payload: { user: parsedUser, token },
          });
        } catch (error) {
          console.error("Error parsing user data:", error);
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          dispatch({ type: "SET_LOADING", payload: false });
        }
      } else {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    };

    initializeAuth();
  }, []);

  // Login function
  const login = async (email, password) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });

      const response = await api.post("/auth/login", { email, password });
      const { user, token } = response.data.data;

      // Store in localStorage
      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(user));

      dispatch({
        type: "LOGIN_SUCCESS",
        payload: { user, token },
      });

      toast.success("Login successful!");
      return { success: true };
    } catch (error) {
      dispatch({ type: "SET_LOADING", payload: false });
      const message = error.response?.data?.message || "Login failed";
      return { success: false, message };
    }
  };

  // Register function
  const register = async (userData) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });

      const response = await api.post("/auth/register", userData);
      const { user, token } = response.data.data;

      // Store in localStorage
      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(user));

      dispatch({
        type: "LOGIN_SUCCESS",
        payload: { user, token },
      });

      toast.success("Registration successful!");
      return { success: true };
    } catch (error) {
      dispatch({ type: "SET_LOADING", payload: false });
      const message = error.response?.data?.message || "Registration failed";
      return { success: false, message };
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      // Clear localStorage
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      clearCheckoutSession();

      dispatch({ type: "LOGOUT" });
      toast.success("Logged out successfully");
    }
  };

  // Update profile function
  const updateProfile = async (profileData) => {
    try {
      const response = await api.put("/auth/profile", profileData);
      const { user } = response.data.data;

      // Update localStorage
      localStorage.setItem("user", JSON.stringify(user));

      dispatch({
        type: "UPDATE_USER",
        payload: user,
      });

      toast.success("Profile updated successfully!");
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || "Profile update failed";
      return { success: false, message };
    }
  };

  // Change password function
  const changePassword = async (passwordData) => {
    try {
      await api.put("/auth/change-password", passwordData);
      toast.success("Password changed successfully!");
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || "Password change failed";
      return { success: false, message };
    }
  };

  // Memoized role checking functions
  const hasRole = useCallback(
    (role) => {
      return state.user?.role === role;
    },
    [state.user?.role]
  );

  const hasAnyRole = useCallback(
    (roles) => {
      return roles.includes(state.user?.role);
    },
    [state.user?.role]
  );

  const isAdmin = useCallback(() => hasRole("admin"), [hasRole]);

  const isModerator = useCallback(
    () => hasAnyRole(["moderator", "admin"]),
    [hasAnyRole]
  );

  const isCustomer = useCallback(
    () => hasAnyRole(["customer", "moderator", "admin"]),
    [hasAnyRole]
  );

  const value = {
    ...state,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    hasRole,
    hasAnyRole,
    isAdmin,
    isModerator,
    isCustomer,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
